import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import Button from 'components/field/Button';
import ScoreCard from 'components/field/ScoreCard';
import TextArea from 'components/field/TextArea';
import Accordion from 'components/global/Accordion';
import Card from 'components/global/Card';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import InfoLabel from './component/InfoLabel';
import * as S from './styled/FeedBack3.styled';

export const feedback3DefaultValues = [
	{
		type: 'color',
		value: '#f5f5f5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#ffffff',
		name: 'formBackgroundColor'
	},

	{
		type: 'color',
		value: '#000000',
		name: 'headerTextColor'
	},

	{
		type: 'color',
		value: '#696969',
		name: 'contentTitleColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'contentTextColor'
	},
	{
		type: 'color',
		value: '#3D5FFF',
		name: 'buttonColor'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	},
	{
		type: 'select',
		value: 'nps',
		name: 'rateDeliveryType'
	},
	{
		type: 'select',
		value: 'nps',
		name: 'rateProductType'
	},
	{
		type: 'input',
		value: null,
		name: 'feedbackDeliveryTitle'
	},
	{
		type: 'input',
		value: null,
		name: 'feedbackProductTitle'
	}
];

const Feedback3 = (props) => {
	const {
		deliveryCompany,
		deliveryCompanyLogo,
		trackingNumber,
		trackingUrl,
		deliveryDate,
		deliveryCompanyAr,
		storeName,
		orderNumber,
		orderItems = [],
		upload = true,
		propJson,
		isMobile,
		otoOrderID
	} = props;

	const { showError, showSuccess } = useAlert();
	const { t } = useTranslation();
	const { LanguageCode } = useLocale();

	const { getLocaleDate, getTimeWithAMPM } = useLocale();
	const isMobileView = useMediaQuery(Breakpoints.mobileLandscape);

	const schema = useMemo(
		() =>
			yup.object().shape({
				rating: yup.number().required(),
				shipmentRating: yup.number().required(),
				rateComment: yup.string().nullable(),
				ratingFile: yup.string().nullable()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			rating: props.rating ?? null,
			shipmentRating: props.shipmentRating ?? null,
			ratingFile: props.ratingFile ?? null,
			rateComment: props.rateComment ?? null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isSubmitting, isSubmitSuccessful }
	} = formMethods;

	const onSubmit = async (data) => {
		try {
			const response = await openAxios.post(OPEN.SMS.UPDATE_FEEDBACK_SMS_DETAILS, {
				otoOrderID: props.otoOrderID,
				...data
			});
			if (response.status === 200) {
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const memoizedItems = useMemo(() => orderItems, []);

	const columns = useMemo(
		() => [
			{
				header: 'item',
				accessorKey: 'productName',
				size: 120,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<S.ItemInfo>
						<Image
							url={row.original.productImage}
							style={{
								maxWidth: '5.5rem',
								maxHeight: '5.5rem',
								objectFit: 'contain',
								borderRadius: '4px'
							}}
						/>

						<Typography fontSize="--paragraph3-font-size" fontWeight="500">
							{row.original.productName}
						</Typography>
					</S.ItemInfo>
				)
			},
			{
				header: 'quantity',
				accessorKey: 'qty',
				size: 30,
				maxSize: 30,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<Typography fontSize="--paragraph3-font-size" fontWeight="500" align="center">
						{row.original.qtyOrdered}
					</Typography>
				)
			}
		],
		[]
	);

	return (
		<FormProvider {...formMethods}>
			<S.Container>
				<Card
					padding={0}
					background
					border={0}
					shadowed
					rounded
					style={{
						backgroundColor: propJson?.formBackgroundColor
					}}>
					<S.FeedBackContentWrapper>
						<InfoLabel
							label="orderInformation"
							value={propJson?.showOrderNumber ? `(${orderNumber})` : ''}
							valueFontWeight="500"
							labelFontWeight="700"
							labelVariant="h4"
							rowMode={!isMobile && !isMobileView}
							labelColor={propJson?.headerTextColor}
							valueColor={propJson?.headerTextColor}
							valueVariant="p1"
							iconColor={propJson?.iconColor}
						/>

						<S.FeedBackInfo className={isMobile ? 'mobileView' : ''}>
							<InfoLabel
								label="storeName"
								value={storeName}
								labelFontWeight="500"
								labelVariant="p2"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								valueFontWeight="500"
								valueVariant="p1"
								className="infoLabel"
							/>
							<S.FeedBackInfoCustomWrapper>
								<Typography fontWeight="500" variant="p2" color={propJson?.contentTitleColor}>
									deliveryCompany
								</Typography>
								<S.FeedBackInfoCustomInfo>
									<S.FeedBackInfoImage>
										<Image url={deliveryCompanyLogo} alt="logo" width="100%" height="100%" />
									</S.FeedBackInfoImage>
									<S.FeedBackInfoContent>
										<Typography color={propJson?.contentTextColor} fontWeight="500" variant="p2">
											{LanguageCode === 'arabicValue' ? deliveryCompanyAr : deliveryCompany}
										</Typography>

										<a
											href={trackingUrl}
											style={{ textDecoration: 'none' }}
											target="_blank"
											rel="noreferrer">
											<Typography variant="p3" noWrap fontWeight="700" color="var(--blue)">
												{trackingNumber}
											</Typography>
										</a>
									</S.FeedBackInfoContent>
								</S.FeedBackInfoCustomInfo>
							</S.FeedBackInfoCustomWrapper>

							<InfoLabel
								label="deliveryDate"
								value={
									<Trans
										i18nKey="feedbackSms3DeliveryDate"
										values={{
											deliveryHour: getTimeWithAMPM(deliveryDate),
											deliveryDate: getLocaleDate(deliveryDate)
										}}
									/>
								}
								labelFontWeight="500"
								labelVariant="p2"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								valueFontWeight="500"
								valueVariant="p1"
								className="infoLabel"
							/>
						</S.FeedBackInfo>
						<S.Line className="line" />
						<Typography
							variant="h4"
							fontWeight="700"
							className="mb-2 mt-4"
							color={propJson?.headerTextColor}>
							feedbackSms
						</Typography>

						<S.FeedBackForm>
							<S.FeedBackFormRow>
								<Typography
									variant="p1"
									fontWeight="700"
									className="mb-2 mt-4"
									color={propJson?.headerTextColor}>
									{propJson?.feedbackDeliveryTitle ?? 'feedbackRateDelivery'}
								</Typography>
								<S.StarWrapper>
									<ScoreCard
										value={watch('shipmentRating')}
										setValue={(value) => {
											setValue('shipmentRating', value, {
												shouldValidate: true,
												shouldDirty: true
											});
										}}
										showText={false}
										size="xxx-large"
										variant={propJson?.rateDeliveryType}
										base={propJson?.rateDeliveryType === 'nps' ? 10 : 5}
										disabled={props.shipmentRating}
									/>
								</S.StarWrapper>
							</S.FeedBackFormRow>

							<S.FeedBackFormRow>
								<Typography
									variant="p1"
									fontWeight="700"
									className="mb-2 mt-4"
									color={propJson?.headerTextColor}>
									{propJson?.feedbackProductTitle ?? 'feedbackRateProduct'}
								</Typography>

								<S.StarWrapper>
									<ScoreCard
										value={watch('rating')}
										setValue={(value) => {
											setValue('rating', value, {
												shouldValidate: true,
												shouldDirty: true
											});
										}}
										showText={false}
										size="xxx-large"
										variant={propJson?.rateProductType}
										base={propJson?.rateProductType === 'nps' ? 10 : 5}
										disabled={props.rating}
									/>
								</S.StarWrapper>
							</S.FeedBackFormRow>

							<S.FeedBackFormRow>
								<InfoLabel
									label="feedbackTextMessage"
									labelVariant="p2"
									labelFontWeight="700"
									value="feedbackOptional"
									valueVariant="p3"
									valueFontWeight="500"
									valueColor={propJson?.contentTitleColor}
									labelColor={propJson?.headerTextColor}
									rowMode
									className="mb-2 mt-4"
								/>

								<TextArea
									placeholder="enterYourNote"
									name="rateComment"
									label="rateComment"
									rows="12"
									style={{
										backgroundColor: propJson?.formBackgroundColor,
										color: propJson?.formTextColor
									}}
									disabled={props.rating}
								/>
							</S.FeedBackFormRow>

							{upload && (
								<S.FeedBackFormRow>
									<InfoLabel
										label="uploadFile"
										labelVariant="p2"
										labelFontWeight="700"
										value="feedbackOptional"
										valueVariant="p3"
										valueFontWeight="500"
										valueColor={propJson?.contentTitleColor}
										labelColor={propJson?.headerTextColor}
										rowMode
										className="mb-2 mt-4"
									/>
									<S.UploadFileWrapper className={isMobile ? 'mobileView' : ''}>
										<AdvanceFileInput
											name="ratingFile"
											acceptedFileType=".png,.jpg,.jpeg,"
											value={watch('ratingFile')}
											setValue={(value) => {
												setValue(`ratingFile`, value ?? null, {
													shouldValidate: true,
													shouldDirty: true
												});
											}}
											maxFileSize={6}
											fileLimit={1}
											height="19rem"
											showDragDropMessage={false}
											disabled={props.rating}
											uploadUrl={OPEN.UPLOAD_FILE}
											additionalParams={{ orderID: otoOrderID }}
										/>
									</S.UploadFileWrapper>

									{!props.ratingFile && (
										<Typography color="var(--content-2)" fontWeight="500">
											{t('msg.feedbackUploadSize')}
										</Typography>
									)}
								</S.FeedBackFormRow>
							)}

							<S.FeedBackFormRow>
								{!props.rating && (
									<Button
										label="submit"
										variant="blue"
										fullWidth
										onClick={handleSubmit(onSubmit, (errors) => {
											if (errors?.rating || errors?.shipmentRating) {
												showError({
													message: 'errorMsg.feedbackRatingsRequired'
												});
											}

											AppLogger.log(errors);
										})}
										style={{ background: propJson?.buttonColor }}
										loading={isSubmitting}
										disabled={isSubmitSuccessful}
										size="xx-large"
									/>
								)}
							</S.FeedBackFormRow>
						</S.FeedBackForm>

						<S.Line className="line" />

						<Accordion
							header="orderItems"
							style={{ width: '100%', marginTop: '4rem' }}
							defaultVisible={true}>
							<div>
								<S.AccordionInner>
									<Table columns={columns} data={memoizedItems} />
								</S.AccordionInner>
							</div>
						</Accordion>
					</S.FeedBackContentWrapper>
				</Card>
			</S.Container>
		</FormProvider>
	);
};

export default Feedback3;
